import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { KeywordSuggestion } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import { Icon } from "../../assets/icons/Icon";
import { formatNumber } from "../../helpers/helpers";
import { getPrice } from "../pricing/utils";
import Button from "../ui/button/Button";
import { Pagination, PaginationConfig } from "../ui/pagination/Pagination";
import ProgressBar from "../ui/progressBar/ProgressBar";
import { SearchBar } from "../ui/search/SearchBar";
import styles from "./KeywordSuggestions.module.scss";
import KeywordAttributes from "./keywordAttributes/KeywordAttributes";

interface KeywordSuggestionsProps {
  setSelectedKeywords: (
    keywords: string[] | ((prev: string[]) => string[]),
  ) => void;
  selectedKeywords: string[];
  onSusess?: () => void;
  siteId?: number;
  withAttributes?: boolean;
  createKeywords?: (data: {
    refresh_frequency_override: number | null;
    location_id?: number;
    country: string;
  }) => void;
}

type KeywordSuggestionColumnSort = {
  id: keyof KeywordSuggestion;
  desc: boolean;
};

const KeywordSuggestions = ({
  setSelectedKeywords,
  selectedKeywords,
  siteId,
  withAttributes,
  createKeywords,
}: KeywordSuggestionsProps) => {
  const queryClient = useQueryClient();
  const [checkAllItems, setCheckAllItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<KeywordSuggestionColumnSort[]>([]);
  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });
  const [isInputActive, setIsInputActive] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: keywords, isPending } = useQuery({
    queryKey: [
      "keywordSuggestions",
      siteId,
      searchTerm,
      pagination.itemsPerPage,
      pagination.currentPage,
      toApiSorting(sorting),
    ],
    queryFn: async () => {
      const response = await apiClient.sites.keywordSuggestionsDetail(
        siteId || 0,
        {
          "page[size]": pagination.itemsPerPage,
          "page[number]": pagination.currentPage,
          s: toApiSorting(sorting),
          q: {
            // @ts-expect-error BE types - missing input field
            name_cont: searchTerm,
          },
        },
      );
      const { headers } = response;

      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count") || 0),
        nextPage: Number(headers.get("x-pager-next-page") || 0),
        prevPage: Number(headers.get("x-pager-prev-page") || 0),
        totalPages: Number(headers.get("x-pager-total-pages") || 1),
      }));

      const resp = response.data?.keyword_suggestions || [];

      return resp;
    },
    enabled: !!siteId,
  });

  useEffect(() => {
    if (keywords && keywords.length > 0) {
      const allSelected = keywords.every((keyword) =>
        selectedKeywords.includes(keyword.name),
      );
      setCheckAllItems(allSelected);
    } else {
      setCheckAllItems(false);
    }
  }, [selectedKeywords, keywords]);

  const columnHelper = createColumnHelper<KeywordSuggestion>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "selection",
        header: () => (
          <input
            type="checkbox"
            checked={checkAllItems}
            onChange={(e) => selectKeywords(e, true)}
            aria-label="Select all rows"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedKeywords.includes(row.original.name)}
            onChange={(e) => {
              const value = row.original.name;
              selectKeywords(e, false, value);
            }}
            value={row.original.name}
            aria-label={`Select ${row.original.name}`}
          />
        ),
        size: 40,
        enableSorting: false,
      }),
      columnHelper.accessor("name", {
        header: "Keyword",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("pos", {
        header: "Rank",
        size: 90,
        cell: (info) => info.getValue() || "N/A",
      }),
      columnHelper.accessor("msv", {
        header: "MSV",
        size: 90,
        cell: (info) => {
          const value = info.getValue();

          return value ? formatNumber(value as number) : "N/A";
        },
      }),
      columnHelper.accessor("cpc", {
        header: "CPC",
        size: 90,
        cell: (info) => getPrice(Number(info.getValue()), "USD") || "N/A",
      }),
      columnHelper.accessor("alli_rating", {
        header: "Score",
        cell: (info) => {
          const score = info.getValue() || 0;

          return (
            // @ts-expect-error TS2339 non-existing className
            <div className={styles.scoreContainer}>
              {/*
               // @ts-expect-error TS2339 non-existing className */}
              <div className={styles.scoreBar}>
                <ProgressBar
                  min={score}
                  max={100}
                  customText={`${Math.ceil(score)} %`}
                />
              </div>
            </div>
          );
        },
        size: 100,
      }),
    ],
    [checkAllItems, selectedKeywords, keywords],
  );

  const table = useReactTable({
    data: keywords || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    state: {
      sorting,
    },
    onSortingChange: (nextState) =>
      setSorting(nextState as unknown as KeywordSuggestionColumnSort[]),
    isMultiSortEvent: () => {
      return true;
    },
  });

  const selectKeywords = (
    e: React.ChangeEvent<HTMLInputElement>,
    checkAll: boolean = false,
    value?: string,
  ) => {
    const isChecked = e.target.checked;

    if (checkAll) {
      if (isChecked) {
        setSelectedKeywords((prev) => {
          const currentKeywords = keywords?.map((item) => item.name) || [];
          const newKeywords = currentKeywords.filter(
            (keyword: string) => !prev.includes(keyword),
          );

          return [...prev, ...newKeywords];
        });
      } else {
        setSelectedKeywords((prev) => {
          const currentKeywords = keywords?.map((item) => item.name) || [];

          return prev.filter((keyword) => !currentKeywords.includes(keyword));
        });
      }
    } else if (value) {
      setSelectedKeywords((prev) => {
        if (prev.includes(value)) {
          return prev.filter((item) => item !== value);
        }

        return [...prev, value];
      });
    }
  };

  const removeKeyword = (keyword: string) => {
    setSelectedKeywords((prev) => prev.filter((item) => item !== keyword));
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsInputActive(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const lines = inputValue
        .split(/\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      setSelectedKeywords((prev) => {
        const newKeywords = lines.filter((line) => !prev.includes(line));

        return [...prev, ...newKeywords];
      });

      setInputValue("");
      inputRef.current?.focus();
    } else if (e.key === "Escape") {
      setIsInputActive(false);
    }
  };

  const handleEmptyStateClick = () => {
    setIsInputActive(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 10);
  };

  return (
    <div className={styles.keywords}>
      <div>
        <div className={styles.keywordsHeader}>
          <h3>Keyword Suggestions</h3>
        </div>
        <div className={styles.keywordsHeaderSearch}>
          <SearchBar
            onChange={setSearchTerm}
            value={searchTerm}
            placeholder="Search"
          />
        </div>

        {/*
         // @ts-expect-error TS2339 non-existing className */}
        <div className={styles.tableContainer}>
          <table className={styles.keywordTable}>
            <thead>
              <tr>
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        userSelect: "none",
                        whiteSpace: "nowrap",
                        ...(header.column.columnDef.size != null &&
                          header.column.columnDef.size !== 150 && {
                            width: header.column.columnDef.size,
                          }),
                      }}
                      role="columnheader"
                    >
                      {header.isPlaceholder ? null : (
                        <>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {(() => {
                            switch (true) {
                              case header.column.getIsSorted() === "asc":
                                return (
                                  <Icon
                                    iconName="SortingIconUp"
                                    className={styles.sortIcon}
                                  />
                                );
                              case header.column.getIsSorted() === "desc":
                                return (
                                  <Icon
                                    iconName="SortingIconDown"
                                    className={styles.sortIcon}
                                  />
                                );
                              case header.column.getCanSort():
                                return (
                                  <Icon
                                    iconName="SortingIcon"
                                    className={styles.sortIcon}
                                  />
                                );
                              default:
                                return null;
                            }
                          })()}
                          {header.column.getCanSort() &&
                            (header.column.getIsSorted() ? (
                              <span className={styles.sortIconOrder}>
                                {sorting.findIndex(
                                  (entry) => entry.id === header.column.id,
                                ) + 1}
                              </span>
                            ) : (
                              // NOTE: reserve space for sorting order indicator
                              <span
                                className={clsx(
                                  styles.sortIconOrder,
                                  styles.invisible,
                                )}
                              >
                                0
                              </span>
                            ))}
                        </>
                      )}
                    </th>
                  )),
                )}
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                Array.from({ length: pagination.itemsPerPage }).map(
                  (_, index) => (
                    <tr key={`skeleton-${index}`}>
                      <td>
                        <Skeleton width={20} height={20} />
                      </td>
                      {Array.from({ length: columns.length - 1 }).map(
                        (_, colIndex) => (
                          <td key={`cell-skeleton-${colIndex}`}>
                            <Skeleton height={20} />
                          </td>
                        ),
                      )}
                    </tr>
                  ),
                )
              ) : keywords?.length === 0 ? (
                <tr>
                  {/*
                   // @ts-expect-error TS2339 non-existing className */}
                  <td colSpan={columns.length} className={styles.emptyTable}>
                    No keywords available
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <Pagination
            {...pagination}
            totalItems={pagination.totalItems}
            onPageChange={(page) => {
              setPagination({ ...pagination, currentPage: page });
              queryClient.invalidateQueries({
                queryKey: ["keywordSuggestions"],
              });
            }}
            onItemsPerPageChange={(items) => {
              setPagination({
                ...pagination,
                itemsPerPage: items,
                currentPage: 1,
              });
              queryClient.invalidateQueries({
                queryKey: ["keywordSuggestions"],
              });
            }}
          />
        </div>
      </div>
      <div className={styles.selectedKeywordsSection}>
        {/*
         // @ts-expect-error TS2551 non-existing className */}
        <div className={styles.selectedKeywords}>
          <div className={styles.selectedKeywordsHeader}>
            <h3>Selected Keywords</h3>

            <div className={styles.selectedKeywordsCount}>
              {selectedKeywords?.length} out of {pagination.totalItems}{" "}
              available
            </div>
          </div>
          <div
            className={styles.selectedKeywordsCard}
            onClick={handleCardClick}
          >
            {selectedKeywords.length > 0 &&
              selectedKeywords.map((keyword) => (
                <div key={keyword} className={styles.selectedKeywordsCardItem}>
                  {keyword}
                  <Button noHover onClick={() => removeKeyword(keyword)}>
                    <Icon iconName="PlainCloseIcon" width="1rem" />
                  </Button>
                </div>
              ))}

            {isInputActive && (
              // @ts-expect-error TS2551 non-existing className
              <div className={styles.invisibleTextareaWprapper}>
                <textarea
                  className={styles.invisibleTextarea}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => {
                    if (!inputValue.trim()) {
                      setIsInputActive(false);
                    }
                  }}
                  autoFocus
                />
              </div>
            )}

            {!selectedKeywords.length && !isInputActive && (
              <div
                // @ts-expect-error TS2339 non-existing className
                className={styles.emptyKeywords}
                onClick={handleEmptyStateClick}
                style={{ cursor: "text" }}
              >
                Start typing your keyword or select suggested
              </div>
            )}
          </div>
          {withAttributes && (
            <div className={styles.selectedKeywordsSectionAttributes}>
              <KeywordAttributes createKeywords={createKeywords} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeywordSuggestions;

const toApiSorting = (sorting: KeywordSuggestionColumnSort[]): string => {
  return sorting
    .map(({ id, desc }) => `${id} ${desc ? "DESC" : "ASC"}`)
    .join(", ");
};
